'use client';

import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

interface TemplatesPanelContextValue {
  isCollapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  /** 左侧模版列表里「查看灵感」打开的 TemplateDetailDrawer */
  isTemplateDetailDrawerOpen: boolean;
  setTemplateDetailDrawerOpen: (open: boolean) => void;
}

const TemplatesPanelContext = createContext<TemplatesPanelContextValue | undefined>(undefined);

export function TemplatesPanelProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isTemplateDetailDrawerOpen, setTemplateDetailDrawerOpen] = useState(false);

  const value = useMemo<TemplatesPanelContextValue>(
    () => ({
      isCollapsed,
      setCollapsed: setIsCollapsed,
      toggleCollapsed: () => setIsCollapsed((v) => !v),
      isTemplateDetailDrawerOpen,
      setTemplateDetailDrawerOpen,
    }),
    [isCollapsed, isTemplateDetailDrawerOpen],
  );

  return <TemplatesPanelContext.Provider value={value}>{children}</TemplatesPanelContext.Provider>;
}

export function useTemplatesPanel(): TemplatesPanelContextValue {
  const ctx = useContext(TemplatesPanelContext);
  if (!ctx) {
    throw new Error('useTemplatesPanel must be used within TemplatesPanelProvider');
  }
  return ctx;
}
